import { forwardRef } from 'react';

import { FieldValues } from 'react-hook-form';
import { useGlobal } from 'src/contexts/GlobalContext';

import DrawerSelect from '../DrawerSelect';
import { StyledSelect } from './CustomSelect.styled';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  field?: FieldValues;
  options: SelectOption[];
  placeholder: string | React.ReactNode;
  disabled?: boolean;
  title: string;
  onOkText: string;
  onOk: (value: string | string[]) => void;
  'data-testid'?: string;
  allowClear?: boolean;
  onClear?: () => void;
  defaultValue?: string;
  mode?: 'tags' | 'default';
  tokenSeparators?: string[];
}

export const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>((props, ref) => {
  const {
    field,
    options,
    placeholder,
    disabled,
    title,
    onOkText,
    onOk,
    'data-testid': dataTestId,
    allowClear,
    onClear,
    defaultValue,
    mode,
    tokenSeparators,
  } = props;
  const { isDesktop } = useGlobal();

  const hasValidOptions = options && options.length > 0;
  const currentValue = field?.value;
  const hasValidValue = hasValidOptions && currentValue && options.some((option) => option.value === currentValue);
  const isEmpty = !currentValue || currentValue === '' || currentValue === null || currentValue === undefined;

  const safeField = hasValidValue ? field : { ...field, value: null };

  const handleClear = () => {
    if (onClear) {
      onClear();
    }
  };

  return isDesktop ? (
    <StyledSelect
      {...safeField}
      key={`desktop-select-${isEmpty}-${field?.value}`}
      placeholder={placeholder}
      options={options}
      disabled={disabled}
      data-testid={dataTestId}
      allowClear={allowClear}
      onClear={handleClear}
      mode={mode === 'tags' ? 'tags' : undefined}
      tokenSeparators={tokenSeparators}
    />
  ) : (
    <DrawerSelect
      {...safeField}
      value={safeField?.value ?? null}
      options={options}
      onOk={onOk}
      title={title}
      placeholder={placeholder}
      onOkText={onOkText}
      disabled={disabled}
      ref={ref}
      data-testid={dataTestId}
      allowClear={allowClear}
      onClear={onClear}
      defaultValue={defaultValue}
      mode={mode === 'tags' ? 'tags' : undefined}
      tokenSeparators={tokenSeparators}
    />
  );
});

CustomSelect.displayName = 'CustomSelect';
